/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import type { RouteParams } from 'vue-router'
import type { InitStage } from '../plugins/00.init'
import type { RouteLocationNormalizedLoaded } from '#vue-router'
import { requestIdleCallback } from '#app'

export function useInited(stage: InitStage): globalThis.ComputedRef<boolean> {
  return useNuxtApp().$initedFunc(stage)
}

export type ValueNotFunction = Record<PropertyKey, unknown> | boolean | string | number | undefined | null | object
export type ValueMaybeFunction<T> = T | (() => T)

export function getFuncValue<T extends ValueNotFunction>(inp: ValueMaybeFunction<T>): T {
  return typeof inp === 'function' ? inp() : inp
}

export function useCleanup() {
  let listeners: (() => void)[] = []

  function listenersSet(arr: typeof listeners) {
    listeners.forEach((i) => typeof i === 'function' && helpers.tryCatch(() => i()))
    listeners = arr
  }

  const off = () => listenersSet([])
  if (getCurrentInstance()) onDeactivated(off)
  if (getCurrentScope()) onScopeDispose(off)

  return (...cleanupFunc: typeof listeners) => {
    listenersSet(cleanupFunc)
    return (): void => off()
  }
}
function createTimeout({ get, set, getGeneration, setGeneration }: { get(): number; set(i: number): void; getGeneration(): number; setGeneration(i: number): void }) {
  let disposed = false

  function clear(dispose?: true): void {
    setGeneration(getGeneration() + 1)

    if (import.meta.client && get() !== -1) {
      window.clearTimeout(get())
      set(-1)
    }
    if (dispose) disposed = true
  }

  if (getCurrentInstance()) onDeactivated(() => clear())
  if (getCurrentScope()) onScopeDispose(() => clear(true))

  return {
    active: () => {
      const index = get()
      return index !== -1 && index !== -2
    },
    clear,
    set(cb: () => unknown, ms: number): void {
      if (!import.meta.client || disposed) return
      clear()
      const currentGeneration = getGeneration()
      set(
        window.setTimeout(() => {
          if (disposed || currentGeneration !== getGeneration()) return
          set(-1)
          requestIdleCallback(() => {
            if (disposed || currentGeneration !== getGeneration()) return
            cb()
          })
        }, ms)
      )
    },
  }
}
export function useTimeoutGlobal(stateName: string) {
  const timerIndex = useState('useTimeoutGlobal:' + stateName, () => -1)
  const generation = useState('useTimeoutGlobalGeneration:' + stateName, () => 0)

  return createTimeout({
    get: () => timerIndex.value,
    set: (i) => (timerIndex.value = i),
    getGeneration: () => generation.value,
    setGeneration: (i) => (generation.value = i),
  })
}

export function useTimeout() {
  let time = -1
  let generation = 0

  return createTimeout({
    get: () => time,
    set: (i) => (time = i),
    getGeneration: () => generation,
    setGeneration: (i) => (generation = i),
  })
}

export function useDelayInit(ms: number, cb?: () => unknown | (() => void)): Readonly<globalThis.Ref<boolean>> {
  const time = useTimeout()
  const init = shallowRef(false)
  const cleanup = useCleanup()

  onNuxtReady(() =>
    time.set(() => {
      const off = cb?.()
      if (typeof off === 'function') cleanup(() => off?.())
      nextTick(() => (init.value = true))
    }, ms)
  )

  return toRef(() => init.value)
}

export function useWatchChanged<T>(inp: { watch: () => T; holder: () => T; update: (i: T) => void; deep?: boolean; immediate?: boolean }) {
  function set(curr: T) {
    const prev = inp.holder()
    if ((!helpers.isDef(curr) && !helpers.isDef(prev)) || curr === prev || isEqualSimple(curr, prev)) return
    inp.update(curr)
  }
  watch(() => inp.watch(), set, { deep: inp.deep, immediate: inp.immediate })
  return { set }
}

export function useValueCache<T>(cb: () => T, updated?: (val: T) => void): Readonly<globalThis.Ref<T>> {
  const holder = shallowRef<T>(cb())
  updated?.(holder.value)
  useWatchChanged({
    deep: true,
    holder: () => holder.value,
    watch: () => cb(),
    update: (curr) => {
      holder.value = curr
      updated?.(holder.value)
    },
  })
  return toRef(() => holder.value as T)
}

export function useParams<T>(toParams: (i: RouteParams, route: RouteLocationNormalizedLoaded) => T): Readonly<globalThis.Ref<T>> {
  const route = useRoute()
  return useValueCache(() => toParams(route.params as RouteParams, route))
}

export function useInterval(
  name: string,
  off?: true
): {
  clear: () => () => void
  set(cb: () => void, ms: number): void
} {
  const cleanup = useCleanup()
  let interval = -1

  return {
    clear: () => cleanup(),
    set(cb: () => void, ms: number) {
      if (!import.meta.client) return
      cleanup()
      interval = window.setInterval(() => {
        if (import.meta.dev && !off) console.log('interval on', name, new Date())
        cb()
      }, ms)
      cleanup(() => window.clearInterval(interval))
    },
  }
}

export function useAnimationFinished(elem: () => HTMLElement | None, timer: ReturnType<typeof useTimeout>) {
  const { start } = useAsyncStopLocal()

  return (inp: { ms: number; done: () => void; fail?: (inp: { done: () => void }) => void }): (() => void) => {
    let stopped = false
    const { stopSync } = start()
    const skip = () => stopSync() || stopped

    timer.set(async () => {
      if (skip()) return inp.fail?.(inp)
      try {
        await Promise.all(helpers.arr(elem()?.getAnimations(), (i) => i.finished))
        if (skip()) return inp.fail?.(inp)
        inp.done()
      } catch {
        inp.fail?.(inp)
      }
    }, inp.ms)

    return () => {
      if (!skip()) timer.clear()
      stopped = true
    }
  }
}

function useAsyncStopBase(timer: { set(i: number): void; get(): number }) {
  return {
    start() {
      const timerThis = timer.get() + 1
      timer.set(timerThis)
      const stopSync = () => timerThis !== timer.get()
      return {
        stopSync,
        stop(ms: number | Promise<NotVoidPrimitives>): Promise<boolean> {
          if (typeof ms === 'number' && ms < 0) return Promise.resolve(stopSync())
          const p = typeof ms === 'number' ? helpers.sleep(ms) : ms
          return p.then(() => stopSync())
        },
      }
    },
  }
}

export function useAsyncStop(key: string) {
  const temp = useState('app:useAsyncStop:' + key, () => shallowRef({ t: -1 }))
  return useAsyncStopBase({ get: () => temp.value.t, set: (t) => (temp.value = { t }) })
}

export function useAsyncStopLocal() {
  let temp = -1
  const stop = () => (temp += 1)
  if (getCurrentInstance()) onDeactivated(stop)
  if (getCurrentScope()) onScopeDispose(stop)
  return useAsyncStopBase({ set: (i) => (temp = i), get: () => temp })
}
